import ScrollArea from "../ui/shared/ScrollArea";
import { ChatHeader } from "./ChatHeader";
import { MessageInput } from "./MessageInput";
import AudioMessage from "./AudioMessage";
import MediaMessage from "./MediaMessage";
import { supabase, type Database } from "@/lib/supabase";
import { getDisplayName } from "@/utils/contactUtils";
import { formatMessageTime } from "@/utils/messageUtils";
import { formatWhatsAppText } from "@/lib/whatsappTextFormat";
import { messageBelongsToContact } from "@/lib/whatsappJid";
import { canDeleteMessage, isMessageDeleted } from "@/lib/messageDeletion";
import { buildMessageIndex, canReplyToMessage, resolveQuotedPreview, type QuotedPreview } from "@/lib/messageReply";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DeleteMessageDialog } from "./DeleteMessageDialog";
import { QuotedMessagePreview } from "./QuotedMessagePreview";
import { useEffect, useRef, useState, useCallback, useMemo, memo } from "react";
import { Ban, Brain, ChevronDown, CornerUpLeft, Repeat, Trash2, X } from "lucide-react";
import { MessageStatusTicks } from "./MessageStatusTicks";
import type { Fluxo } from "@/hooks/useFluxos";
import type { DeleteMessageResult } from "@/hooks/useMessagesPaginated";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type AccountMessage = Database['public']['Tables']['account_messages']['Row']
type AccountContact = Database['public']['Tables']['account_contacts']['Row']

interface OptimisticMessage extends Omit<AccountMessage, 'id'> {
  id: string | number
  isOptimistic?: boolean
  isSending?: boolean
  sendError?: string
  account_contact_id?: string | null
}
type AssistantNamesMap = Record<string, string>;

/** Sinaliza a intenção de apagar; a confirmação acontece uma única vez em ChatArea. */
type RequestDeleteHandler = (message: OptimisticMessage) => void;
type RequestReplyHandler = (message: OptimisticMessage) => void;

interface MessageActionsProps {
  onRequestDelete?: RequestDeleteHandler;
  onRequestReply?: RequestReplyHandler;
  /** Nome do contato, exibido como autor no bloco de citação. */
  contactName?: string | null;
}

interface MessageBubbleProps extends MessageActionsProps {
  message: OptimisticMessage;
  sender: "user" | "bot";
  instance?: string;
  apiKey?: string;
  assistantNamesMap?: AssistantNamesMap;
  quotedPreview?: QuotedPreview<OptimisticMessage> | null;
}

interface MessageItemProps extends MessageActionsProps {
  message: OptimisticMessage;
  instance?: string;
  apiKey?: string;
  assistantNamesMap?: AssistantNamesMap;
  /** Índice já pronto; a citação é resolvida aqui dentro para o memo poder barrar o render. */
  messageIndex: Map<string, OptimisticMessage>;
}

interface MessagesAreaProps extends MessageActionsProps {
  /** Id do contato: âncora estável de "que conversa é esta". Ver o efeito de troca. */
  conversationId: string;
  messages: OptimisticMessage[];
  loading: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  instance?: string;
  apiKey?: string;
  assistantNamesMap?: AssistantNamesMap;
}

interface ChatAreaProps {
  selectedContact: AccountContact | null;
  messages: OptimisticMessage[];
  onSendImage: (imageBase64: string, caption?: string, fileName?: string) => Promise<boolean>;
  onSendVideo: (videoBase64: string, caption?: string, fileName?: string) => Promise<boolean>;
  onSendDocument: (documentBase64: string, fileName: string, caption?: string) => Promise<boolean>;
  /** Recebe o texto já trimado do compositor e responde se ele saiu. */
  onSendMessage: (text: string) => Promise<boolean>;
  onSendAudio: (audioUrl: string) => Promise<boolean>;
  onSendFluxo?: (fluxo: Fluxo) => void;
  loading: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  sending?: boolean;
  instance?: string;
  apiKey?: string;
  onContactUpdate?: () => void;
  onContactUpdateOptimistic?: (updates: Partial<AccountContact>) => void;
  /** Ausente quando a conta não roda na stack v3 — a ação de apagar fica oculta. */
  onDeleteMessage?: (message: OptimisticMessage) => Promise<DeleteMessageResult>;
  /** Mensagem sendo respondida; controlada por ChatInterface, que envia o quoted. */
  replyingTo?: OptimisticMessage | null;
  onReplyingToChange?: (message: OptimisticMessage | null) => void;
}

/** Quantas bolhas ficam montadas de uma vez. Ver o comentário em MessagesArea. */
const RENDER_WINDOW = 150

/** Âncora estável para rolar até a mensagem citada. */
const messageAnchorId = (id: string | number) => `chat-msg-${id}`;

function scrollToMessage(id: string | number) {
  const element = document.getElementById(messageAnchorId(id));
  if (!element) return;

  element.scrollIntoView({ behavior: "smooth", block: "center" });
  // Pisca a bolha para o olho encontrar onde parou.
  element.classList.add("ring-2", "ring-primary/60", "rounded-2xl");
  window.setTimeout(() => {
    element.classList.remove("ring-2", "ring-primary/60", "rounded-2xl");
  }, 1200);
}

/** Bolha neutra que substitui a mensagem apagada, no lugar original da conversa. */
const DeletedMessageBubble = ({ message, sender }: { message: OptimisticMessage; sender: "user" | "bot" }) => {
  const { t } = useTranslation("chat");

  return (
    <div className={`flex flex-col ${sender === "user" ? "items-end" : "items-start"}`}>
      <div
        className={`max-w-[220px] px-3 py-2 rounded-2xl border border-dashed border-border bg-transparent ${
          sender === "user" ? "rounded-br-sm" : "rounded-bl-sm"
        }`}
      >
        <p className="flex items-center gap-1.5 text-[13px] italic text-muted-foreground dark:text-neutral-400">
          <Ban className="w-3 h-3 shrink-0" />
          {t("area.deletedByYou")}
        </p>
        <div className="mt-2 pt-2 border-t border-border/50 dark:border-white/10 text-xs text-muted-foreground dark:text-neutral-400">
          {formatMessageTime(message.sent_at)}
        </div>
      </div>
    </div>
  );
};

const MessageBubble = ({ message, sender, instance, apiKey, assistantNamesMap, onRequestDelete, onRequestReply, contactName, quotedPreview }: MessageBubbleProps) => {
  const { t } = useTranslation("chat");
  const [menuOpen, setMenuOpen] = useState(false);
  const [localTranscription, setLocalTranscription] = useState<string | null>(
    (message as any).transcription || null
  );
  const [localProcessedUrl, setLocalProcessedUrl] = useState<string | null>(
    (message as any).processed_media_url || (message as any).media_url || null
  );

  const messageTime = formatMessageTime(message.sent_at);
  const isAudio = message.media_kind === 'audioMessage' || message.media_kind === 'audio';
  const isImage = message.media_kind === 'imageMessage' || message.media_kind === 'image';
  const isVideo = message.media_kind === 'videoMessage' || message.media_kind === 'video';
  const isDocument = message.media_kind === 'documentMessage' || message.media_kind === 'document';
  const hasMedia = isAudio || isImage || isVideo || isDocument;
  const isAiMessage = message.from_me && message.is_from_ai;
  const isFollowupMessage = message.from_me && (message as any).is_followup === true;
  // Só o canal oficial reporta o ciclo de entrega (webhook `statuses`). Nas stacks
  // não oficiais o status fica congelado em 'SENT', então não exibimos check lá.
  const isMetaChannelMessage =
    (message.raw_payload as { channel?: string } | null | undefined)?.channel === 'meta';

  const messageId = typeof message.id === 'string' ? message.id : String(message.id);

  // Check if media_url is from WhatsApp (mmg.whatsapp.net)
  const mediaUrl = (message as any).media_url;
  const isWhatsAppMedia = mediaUrl && typeof mediaUrl === 'string' && mediaUrl.includes('mmg.whatsapp.net');

  // Check if this is a document sent from dashboard (stored in our Supabase Storage)
  const rawPayload = (message as any).raw_payload;
  const isSentDocument = message.from_me &&
                         rawPayload?.sent_via_dashboard === true &&
                         rawPayload?.storage_bucket === 'sent-documents';

  // Check if media_url is from Supabase Storage
  const isSupabaseStorage = mediaUrl && typeof mediaUrl === 'string' &&
                           (mediaUrl.includes('supabase.co/storage') || mediaUrl.includes('/storage/v1/object/public/'));

  // Determine effective media URL with priority:
  // 1. processed_media_url if available (already processed)
  // 2. media_url if it's a document sent from dashboard (already in our storage)
  // 3. media_url if it's from Supabase Storage (images/videos/audio sent by us)
  // 4. media_url if it's from WhatsApp (needs processing)
  // 5. null (no media available)
  const effectiveMediaUrl = (message as any).processed_media_url ||
                           (isSentDocument ? mediaUrl : null) ||
                           (isSupabaseStorage ? mediaUrl : null) ||
                           (isWhatsAppMedia ? mediaUrl : null);

  const assistantId = (message as any).account_assistant_id;
  const assistantName = assistantId && assistantNamesMap ? assistantNamesMap[assistantId] : null;

  const canDelete = Boolean(onRequestDelete) && canDeleteMessage(message);
  const canReply = Boolean(onRequestReply) && canReplyToMessage(message);
  const hasMenu = canDelete || canReply;

  const getBubbleBackground = () => {
    if (sender === "bot") {
      return "bg-white border border-border shadow-sm dark:bg-neutral-600 dark:border-transparent dark:shadow-none";
    }
    if (isAiMessage) {
      return "bg-blue-50 border border-blue-100 dark:bg-[#1a365d] dark:border-transparent";
    }
    if (isFollowupMessage) {
      return "bg-violet-50 border border-violet-100 dark:bg-[#3b2f5e] dark:border-transparent";
    }
    return "bg-[#DCF8C6] border border-[#c5e8b0] shadow-sm dark:bg-[#005C4B] dark:border-transparent dark:shadow-none";
  };

  return (
    <div
      className={`flex flex-col ${
        sender === "user" ? "items-end" : "items-start"
      }`}
    >
      <div
        className={`group ${hasMedia ? 'max-w-md' : 'max-w-[220px]'} px-3 py-2 rounded-2xl text-foreground dark:text-neutral-50 relative ${
          getBubbleBackground()
        } ${
          sender === "user" ? "rounded-br-sm" : "rounded-bl-sm"
        } ${message.isOptimistic && message.isSending ? 'opacity-70' : ''} ${message.sendError ? 'border border-red-500/50' : ''}`}
      >
        {hasMenu && (
          <Popover open={menuOpen} onOpenChange={setMenuOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={t("area.messageActions")}
                // Fica fora da bolha (mesma âncora dos indicadores de envio) para
                // nunca cobrir o texto da mensagem.
                className={`absolute -top-1.5 -right-1.5 z-10 inline-flex h-5 w-5 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-opacity hover:text-foreground focus-visible:opacity-100 dark:border-[#3F3F464D] dark:bg-[#18181B] dark:text-neutral-300 dark:hover:text-white ${
                  menuOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                }`}
              >
                <ChevronDown className="h-3 w-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="end"
              className="!bg-card p-2 w-[200px] flex flex-col gap-0.5 border-border border shadow-lg rounded-[14px] dark:!bg-[#18181B] dark:border-[#3F3F464D]"
            >
              {canReply && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onRequestReply?.(message);
                  }}
                  className="flex items-center gap-2 rounded-[10px] px-2.5 py-2 text-left text-sm text-foreground transition-colors hover:bg-hover dark:hover:bg-[#27272A]"
                >
                  <CornerUpLeft className="h-4 w-4" />
                  {t("area.replyMessage")}
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onRequestDelete?.(message);
                  }}
                  className="flex items-center gap-2 rounded-[10px] px-2.5 py-2 text-left text-sm text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
                >
                  <Trash2 className="h-4 w-4" />
                  {t("area.deleteMessage")}
                </button>
              )}
            </PopoverContent>
          </Popover>
        )}

        {message.isOptimistic && message.isSending && (
          <div className="absolute -top-1 -right-1 w-3 h-3">
            <div className="w-3 h-3 border-2 border-neutral-300 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        {message.sendError && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
            <span className="text-white text-xs">!</span>
          </div>
        )}

        {quotedPreview && (
          <QuotedMessagePreview
            text={quotedPreview.text}
            fromMe={quotedPreview.fromMe}
            mediaKind={quotedPreview.mediaKind}
            contactName={contactName}
            onClick={quotedPreview.original ? () => scrollToMessage(quotedPreview.original!.id) : undefined}
          />
        )}

        {message.text_content && !hasMedia && <p className="text-[13px] whitespace-pre-wrap break-words">{formatWhatsAppText(message.text_content)}</p>}

        {isAudio && message.wa_message_id && (
          <AudioMessage
            messageId={messageId}
            waMessageId={message.wa_message_id}
            existingProcessedUrl={effectiveMediaUrl}
            transcription={localTranscription}
            onUrlProcessed={(url) => setLocalProcessedUrl(url)}
            onTranscriptionProcessed={(transcription) => setLocalTranscription(transcription)}
            isSending={message.isSending}
          />
        )}

        {isImage && message.wa_message_id && (
          <MediaMessage
            messageId={messageId}
            waMessageId={message.wa_message_id}
            mediaType="image"
            existingProcessedUrl={effectiveMediaUrl}
          />
        )}

        {isVideo && message.wa_message_id && (
          <MediaMessage
            messageId={messageId}
            waMessageId={message.wa_message_id}
            mediaType="video"
            existingProcessedUrl={effectiveMediaUrl}
          />
        )}

        {isDocument && message.wa_message_id && (
          <MediaMessage
            messageId={messageId}
            waMessageId={message.wa_message_id}
            mediaType="document"
            existingProcessedUrl={effectiveMediaUrl}
            isSending={message.isSending}
            fileName={(message.raw_payload as any)?.fileName}
          />
        )}

        {message.text_content && hasMedia && (
          <p className="text-[13px] mt-2 whitespace-pre-wrap break-words">{formatWhatsAppText(message.text_content)}</p>
        )}

        <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/50 dark:border-white/10">
          <div className={`flex items-center gap-1 text-xs ${message.sendError ? 'text-red-500 dark:text-red-300' : 'text-muted-foreground dark:text-neutral-300'}`}>
            <span>{message.sendError ? 'Erro ao enviar' : messageTime}</span>
            {sender === "user" && isMetaChannelMessage && !message.isOptimistic && (
              <MessageStatusTicks status={message.status} />
            )}
          </div>
          {isAiMessage && (
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary dark:bg-[#1a365d]/50 dark:text-white/90">
              <Brain className="w-2.5 h-2.5" />
              <span className="text-[10px] font-medium">
                Assistente - {assistantName || 'IA'}
              </span>
            </div>
          )}
          {isFollowupMessage && !isAiMessage && (
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-[#3b2f5e]/60 dark:text-white/90">
              <Repeat className="w-2.5 h-2.5" />
              <span className="text-[10px] font-medium">
                {t("area.followUp")}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Memoizado: sem isso, cada render de ChatArea reconciliava todas as bolhas da conversa —
 * e cada bolha custa um Popover do Radix mais a formatação da data.
 */
const MessageItem = memo(({ message, instance, apiKey, assistantNamesMap, onRequestDelete, onRequestReply, contactName, messageIndex }: MessageItemProps) => {
  const sender = message.from_me ? "user" : "bot";
  const quotedPreview = useMemo(
    () => resolveQuotedPreview(message, messageIndex),
    [message, messageIndex]
  );

  return (
    <div
      id={messageAnchorId(message.id)}
      className={`flex items-start space-x-3 ${
        sender === "user" ? "flex-row-reverse space-x-reverse" : ""
      }`}
    >
      {isMessageDeleted(message) ? (
        <DeletedMessageBubble message={message} sender={sender} />
      ) : (
        <MessageBubble
          message={message}
          sender={sender}
          instance={instance}
          apiKey={apiKey}
          assistantNamesMap={assistantNamesMap}
          onRequestDelete={onRequestDelete}
          onRequestReply={onRequestReply}
          contactName={contactName}
          quotedPreview={quotedPreview}
        />
      )}
    </div>
  );
});
MessageItem.displayName = "MessageItem";

const MessagesArea = ({ conversationId, messages, loading, hasMore, isLoadingMore, onLoadMore, instance, apiKey, assistantNamesMap, onRequestDelete, onRequestReply, contactName }: MessagesAreaProps) => {
  const { t } = useTranslation("chat");
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const topSentinelRef = useRef<HTMLDivElement>(null)
  const [isNearBottom, setIsNearBottom] = useState(true)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const previousMessagesLengthRef = useRef(messages.length)
  // Índice por wa_message_id (serializado e stanzaId cru) para resolver as
  // citações sem varrer a lista a cada bolha.
  const messageIndex = useMemo(() => buildMessageIndex(messages), [messages])
  const previousScrollHeightRef = useRef(0)
  const isLoadingMoreRef = useRef(false)

  // Teto de bolhas montadas no DOM.
  //
  // Cada bolha monta um Popover do Radix, então o custo de uma conversa longa é o número de
  // nós, não o de renders (os memos já resolveram esses). Virtualizar com react-window
  // resolveria também, mas o List da v2 é dono do próprio container de rolagem: o onScroll,
  // o root do observer, o ajuste de scrollTop ao prepender e a âncora de conversa teriam de
  // ser reescritos contra a API imperativa dela — e prepender desloca todos os índices e o
  // cache de alturas medidas, que é o caso mais frágil de virtualização. Um teto simples
  // entrega o mesmo DOM limitado sem tocar em nada disso.
  const [renderCap, setRenderCap] = useState(RENDER_WINDOW)

  // O estado alimenta o botão flutuante; a ref alimenta os efeitos. Se `scrollToBottom`
  // dependesse do estado, rolar para cima trocaria a identidade da função, o que reexecutaria
  // o efeito de troca de conversa — que força o scroll para o fim. Era o efeito "ímã".
  const isNearBottomRef = useRef(true)
  const setNearBottom = useCallback((value: boolean) => {
    isNearBottomRef.current = value
    setIsNearBottom(value)
  }, [])

  // Verifica se o usuário está próximo do final
  const checkIfNearBottom = useCallback(() => {
    if (!scrollContainerRef.current) return

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
    const threshold = 100 // pixels do final
    const isNear = scrollHeight - scrollTop - clientHeight < threshold
    setNearBottom(isNear)
  }, [setNearBottom])

  // Scroll para o final apenas se estiver próximo. Identidade estável, sempre.
  const scrollToBottom = useCallback((force = false) => {
    if (messagesEndRef.current && (isNearBottomRef.current || force)) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' })
    }
  }, [])

  // Ler scrollTop/scrollHeight força layout, então agrupamos as leituras num frame:
  // um arrasto de rolagem dispara dezenas de eventos e só o último estado importa.
  const scrollFrameRef = useRef<number | null>(null)
  const handleScroll = useCallback(() => {
    if (scrollFrameRef.current !== null) return
    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = null
      checkIfNearBottom()
    })
  }, [checkIfNearBottom])

  useEffect(() => () => {
    if (scrollFrameRef.current !== null) window.cancelAnimationFrame(scrollFrameRef.current)
  }, [])

  // Acompanhar mensagem nova — só quando a lista de fato cresce, e só se o usuário já
  // estiver no fim. Lê a ref, não o estado: mudar de posição de rolagem não pode reexecutar
  // este efeito, senão qualquer scroll vira um scroll forçado.
  useEffect(() => {
    if (messages.length === 0) return

    const hasNewMessage = messages.length > previousMessagesLengthRef.current
    previousMessagesLengthRef.current = messages.length

    if (hasNewMessage && isNearBottomRef.current) {
      scrollToBottom()
    }
  }, [messages, scrollToBottom])

  // Trocar de conversa volta para o fim. Ancorado no id do contato, não no `remote_jid` da
  // primeira mensagem: aquele alterna entre as formas @s.whatsapp.net e @lid conforme o
  // histórico é paginado, e cada alternância virava um scroll forçado.
  // `scrollToBottom` e `setNearBottom` têm identidade estável, então rolar não dispara isto.
  useEffect(() => {
    if (!conversationId) return

    setNearBottom(true)
    setRenderCap(RENDER_WINDOW)
    // Zera o contador para a chegada da primeira página contar como "mensagem nova" — é o
    // efeito acima que rola quando ela chega, mesmo que demore mais que o timer abaixo.
    previousMessagesLengthRef.current = 0

    const timer = window.setTimeout(() => {
      scrollToBottom(true) // reancora depois que mídia e fontes assentam o layout
    }, 150)

    return () => window.clearTimeout(timer)
  }, [conversationId, scrollToBottom, setNearBottom])

  // `onLoadMore` é recriado sempre que o objeto do contato troca de identidade. Sem a ref,
  // o observer era desmontado e remontado o tempo todo — e cada remontagem disparava uma
  // nova página, empilhando histórico que ninguém pediu.
  const onLoadMoreRef = useRef(onLoadMore)
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore
  }, [onLoadMore])

  // Configurar IntersectionObserver para detectar scroll no topo
  useEffect(() => {
    if (!topSentinelRef.current || !scrollContainerRef.current || !hasMore || loading) return

    const currentSentinel = topSentinelRef.current

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && !isLoadingMoreRef.current && hasMore) {
          isLoadingMoreRef.current = true
          onLoadMoreRef.current()
        }
      },
      {
        // Precisa ser o elemento que realmente rola; um root que não clipa nada deixa a
        // sentinela permanentemente "visível".
        root: scrollContainerRef.current,
        rootMargin: '50px',
        threshold: 0.1
      }
    )

    observer.observe(currentSentinel)

    return () => {
      try {
        observer.disconnect()
      } catch (error) {
        console.warn('⚠️ [MessagesArea] Error disconnecting observer:', error)
      }
    }
  }, [hasMore, loading])

  // Reset loading flag quando isLoadingMore mudar
  useEffect(() => {
    if (!isLoadingMore) {
      isLoadingMoreRef.current = false
    }
  }, [isLoadingMore])

  // Manter posição do scroll ao carregar mensagens antigas
  useEffect(() => {
    if (!scrollContainerRef.current || !isLoadingMore) return

    const scrollContainer = scrollContainerRef.current
    previousScrollHeightRef.current = scrollContainer.scrollHeight
  }, [isLoadingMore])

  useEffect(() => {
    if (!scrollContainerRef.current || isLoadingMore) return

    const scrollContainer = scrollContainerRef.current
    const previousScrollHeight = previousScrollHeightRef.current
    if (previousScrollHeight <= 0) return

    // Consome a marca: sem isso, a próxima mensagem que chegasse também deslocaria a
    // rolagem, porque a lista continua crescendo depois que o histórico já foi ajustado.
    previousScrollHeightRef.current = 0

    const currentScrollHeight = scrollContainer.scrollHeight
    if (currentScrollHeight > previousScrollHeight) {
      scrollContainer.scrollTop += currentScrollHeight - previousScrollHeight
    }
  }, [messages.length, renderCap, isLoadingMore])

  // Atualizar botão de scroll e contador de não lidas
  useEffect(() => {
    const shouldShow = !isNearBottom && messages.length > 0
    setShowScrollButton(shouldShow)

    if (!isNearBottom) {
      const lastMessages = messages.slice(-5)
      const unreadMessages = lastMessages.filter(msg => !msg.from_me)
      setUnreadCount(unreadMessages.length)
    } else {
      setUnreadCount(0)
    }
  }, [isNearBottom, messages])

  // A fatia realmente montada. Guardar tudo em memória é barato; montar tudo não é.
  const visibleMessages = useMemo(
    () => (messages.length > renderCap ? messages.slice(-renderCap) : messages),
    [messages, renderCap]
  )
  const hasHiddenOlder = messages.length > renderCap

  // Revelar o que já está carregado antes de pedir mais ao banco. Marca a altura para o
  // efeito de preservação de posição reancorar, igual ao fluxo de carregar histórico.
  const revealOlder = useCallback(() => {
    if (scrollContainerRef.current) {
      previousScrollHeightRef.current = scrollContainerRef.current.scrollHeight
    }
    setRenderCap((cap) => cap + RENDER_WINDOW)
  }, [])

  // Handler para clicar no botão de scroll
  const handleScrollToBottom = useCallback(() => {
    setNearBottom(true)
    scrollToBottom(true)
    setUnreadCount(0)
  }, [scrollToBottom, setNearBottom])

  // Verificar posição inicial
  useEffect(() => {
    checkIfNearBottom()
  }, [checkIfNearBottom])

  if (loading) {
    return (
      <ScrollArea classNames={{ root: "flex-1 p-3 h-full" }}>
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-start space-x-3 animate-pulse">
              <div className="w-8 h-8 bg-card-muted dark:bg-neutral-600 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-card-muted dark:bg-neutral-600 rounded mb-2"></div>
                <div className="h-3 bg-card-muted dark:bg-neutral-600 rounded w-3/4"></div>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    );
  }

  if (messages.length === 0) {
    return (
      <ScrollArea classNames={{ root: "flex-1 h-full" }}>
        <div className="p-4 flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 bg-card-muted dark:bg-neutral-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-muted-foreground dark:text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-foreground dark:text-neutral-50 mb-2">{t("area.noMessages")}</h3>
            <p className="text-muted-foreground dark:text-neutral-300 text-sm">
              {t("area.startConversation")}
            </p>
          </div>
        </div>
      </ScrollArea>
    );
  }

  return (
    <div className="relative flex-1 h-full">
      {/* Mesmas classes que ScrollArea aplicaria; aqui o elemento que rola é o que carrega a
          ref, para o onScroll disparar e o observer/ajuste de posição terem um root real. */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="table-scrollbar w-full overflow-auto flex-1 h-full"
        style={{ scrollBehavior: 'auto' }}
      >
        <div className="p-3 space-y-2">
          {/* Sentinela no topo para paginação. Só arma quando não há mensagem já carregada
              escondida pelo teto — senão pediria página nova ao banco tendo o que mostrar. */}
          {hasMore && messages.length > 0 && !hasHiddenOlder && (
            <div ref={topSentinelRef} className="h-1" />
          )}

          {hasHiddenOlder && (
            <div className="flex items-center justify-center py-3">
              <button
                type="button"
                onClick={revealOlder}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground dark:border-[#3F3F464D] dark:bg-[#18181B] dark:text-neutral-300 dark:hover:text-white"
              >
                {t("area.showOlder")}
              </button>
            </div>
          )}

          {/* Indicador de carregamento no topo */}
          {isLoadingMore && (
            <div className="flex items-center justify-center py-3">
              <div className="flex items-center gap-2 text-muted-foreground dark:text-neutral-400 text-sm">
                <div className="w-4 h-4 border-2 border-muted-foreground dark:border-neutral-400 border-t-transparent rounded-full animate-spin"></div>
                <span>{t("area.loadingOld")}</span>
              </div>
            </div>
          )}

          {/* Indicador de início da conversa */}
          {!hasMore && !loading && messages.length > 0 && (
            <div className="flex items-center justify-center py-3">
              <div className="text-muted-foreground dark:text-neutral-500 text-xs">
                {t("area.conversationStart")}
              </div>
            </div>
          )}

          {/* Mensagens */}
          {visibleMessages.map((message) => (
            <MessageItem
              key={message.id}
              message={message}
              instance={instance}
              apiKey={apiKey}
              assistantNamesMap={assistantNamesMap}
              onRequestDelete={onRequestDelete}
              onRequestReply={onRequestReply}
              contactName={contactName}
              messageIndex={messageIndex}
            />
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Botão flutuante para novas mensagens */}
      {showScrollButton && (
        <button
          onClick={handleScrollToBottom}
          className="absolute bottom-4 right-4 bg-primary hover:bg-primary-hover text-primary-foreground dark:bg-neutral-600 dark:hover:bg-neutral-500 dark:text-white rounded-full p-3 shadow-lg transition-all duration-200 flex items-center gap-2 z-10"
          aria-label={t("area.goToNew")}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
          {unreadCount > 0 && (
            <span className="bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
              {unreadCount}
            </span>
          )}
        </button>
      )}
    </div>
  );
};

export const ChatArea = ({
  selectedContact,
  messages,
  onSendMessage,
  onSendAudio,
  onSendImage,
  onSendVideo,
  onSendDocument,
  onSendFluxo,
  loading,
  hasMore,
  isLoadingMore,
  onLoadMore,
  sending = false,
  instance,
  apiKey,
  onContactUpdate,
  onContactUpdateOptimistic,
  onDeleteMessage,
  replyingTo = null,
  onReplyingToChange
}: ChatAreaProps) => {
  const { t } = useTranslation("chat");
  const [assistantNamesMap, setAssistantNamesMap] = useState<AssistantNamesMap>({})
  const [messageToDelete, setMessageToDelete] = useState<OptimisticMessage | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const contactDisplayName = selectedContact ? getDisplayName(selectedContact) : null

  const handleConfirmDelete = useCallback(async () => {
    if (!messageToDelete || !onDeleteMessage) return

    setIsDeleting(true)
    try {
      const result = await onDeleteMessage(messageToDelete)
      if (!result.ok) {
        toast.error(result.error || t("area.deleteError"))
      }
    } finally {
      setIsDeleting(false)
      setMessageToDelete(null)
    }
  }, [messageToDelete, onDeleteMessage, t])

  const aiMessageAssistantIds = useMemo(() => {
    const ids = new Set<string>()
    messages.forEach(msg => {
      const assistantId = (msg as any).account_assistant_id
      if (msg.is_from_ai && assistantId) {
        ids.add(assistantId)
      }
    })
    return Array.from(ids)
  }, [messages])

  useEffect(() => {
    const fetchAssistantNames = async () => {
      if (!supabase || aiMessageAssistantIds.length === 0) return

      const missingIds = aiMessageAssistantIds.filter(id => !assistantNamesMap[id])
      if (missingIds.length === 0) return

      try {
        const { data, error } = await supabase
          .from('account_assistants')
          .select('id, name')
          .in('id', missingIds)

        if (error) throw error

        if (data && data.length > 0) {
          setAssistantNamesMap(prev => {
            const newMap = { ...prev }
            data.forEach(assistant => {
              newMap[assistant.id] = assistant.name
            })
            return newMap
          })
        }
      } catch (error) {
        console.error('Error fetching assistant names:', error)
      }
    }

    fetchAssistantNames()
  }, [aiMessageAssistantIds])

  // Filtrar mensagens do contato selecionado (JID normalizado + account_contact_id)
  const filteredMessages = useMemo(() => {
    if (!selectedContact || messages.length === 0) return messages

    return messages.filter((msg) => {
      if (messageBelongsToContact(msg, selectedContact)) return true

      console.warn('[ChatArea] Filtering message that does not belong to contact:', {
        messageJid: msg.remote_jid,
        expectedJid: selectedContact.jid,
        messageContactId: msg.account_contact_id,
        expectedContactId: selectedContact.id,
        messageId: msg.id
      })
      return false
    })
  }, [selectedContact, messages])

  if (!selectedContact) {
    return (
      <div className="w-full h-full flex flex-col border rounded-xl bg-card border-border shadow-sm dark:bg-[#20202040] dark:border-[#3F3F464D] dark:shadow-none max-w-[720px] mx-auto">
        <div className="flex-1 flex items-center justify-center chat-wallpaper rounded-xl">
          <div className="text-center">
            <div className="w-16 h-16 bg-card-muted dark:bg-neutral-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-muted-foreground dark:text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-foreground dark:text-neutral-50 mb-2">{t("area.pickConversation")}</h3>
            <p className="text-muted-foreground dark:text-neutral-300 text-sm">
              {t("area.pickConversationHint")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col border rounded-xl bg-card border-border shadow-sm dark:bg-[#20202040] dark:border-[#3F3F464D] dark:shadow-none max-w-[720px] mx-auto overflow-hidden">
      <ChatHeader 
        chatName={getDisplayName(selectedContact)} 
        isGroup={selectedContact.is_group}
        selectedContact={selectedContact}
        onContactUpdate={onContactUpdate}
      />
      <div className="flex-1 overflow-hidden w-full min-w-[280px] chat-wallpaper">
        <MessagesArea
          conversationId={selectedContact.id}
          messages={filteredMessages}
          loading={loading}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={onLoadMore}
          instance={instance}
          apiKey={apiKey}
          assistantNamesMap={assistantNamesMap}
          onRequestDelete={onDeleteMessage ? setMessageToDelete : undefined}
          onRequestReply={onReplyingToChange}
          contactName={contactDisplayName}
        />
      </div>
      <div className="border-t border-border bg-card dark:border-transparent dark:bg-transparent p-3">
        <MessageInput
          onSendMessage={onSendMessage}
          onSendAudio={onSendAudio}
          onSendImage={onSendImage}
          onSendVideo={onSendVideo}
          onSendDocument={onSendDocument}
          onSendFluxo={onSendFluxo}
          sending={sending}
          selectedContact={selectedContact}
          replyBar={replyingTo ? (
            <div className="flex items-start gap-2 rounded-[10px] bg-card-muted px-2 py-1.5 dark:bg-[#27272A]">
              <div className="min-w-0 flex-1">
                <QuotedMessagePreview
                  text={replyingTo.text_content ?? null}
                  fromMe={replyingTo.from_me === true}
                  mediaKind={replyingTo.media_kind}
                  contactName={contactDisplayName}
                  variant="composer"
                />
              </div>
              <button
                type="button"
                aria-label={t("area.replyCancel")}
                onClick={() => onReplyingToChange?.(null)}
                className="mt-0.5 shrink-0 rounded-full p-1 text-muted-foreground transition-colors hover:bg-black/5 hover:text-foreground dark:hover:bg-white/10"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : null}
        />
      </div>

      <DeleteMessageDialog
        open={messageToDelete !== null}
        onClose={() => setMessageToDelete(null)}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
};
